/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Box, Toolbar, Typography} from "@mui/material";
import {theme} from "../../../../constants/theme";
import React, {useCallback, useEffect, useRef, useState} from "react";
import CloseGlobalDialogButton from "../../../components/Global/Dialog/CloseGlobalDialogButton";
import ArgosFlexbox from "../../../components/Containers/ArgosFlexbox";
import {useAppDispatch, useAppSelector} from "../../../../redux/hooks";
import {
  EAdasProgramsSocketRequestTypes,
  selectAdasProgramsMetaData, selectSensingSystemRequirementsLoadingState,
  selectSensingSystemRequirementsTableDataSetStatusMap,
  selectSensingSystemRequirementsTableRowOrder, selectSensorComponentRequirementsLoadingState,
  selectSensorComponentRequirementsTableDataSetStatusMap,
  selectSensorComponentRequirementsTableRowOrder, selectWorldModelSolutionRequirementsLoadingState,
  selectWorldModelSolutionRequirementsTableDataSetStatusMap,
  selectWorldModelSolutionRequirementsTableRowOrder,
  setAdasProgramsMetaData,
  setCoverageRequirementsStudies, setSensingSystemRequirementsLoadingState,
  setSensingSystemRequirementsTableDataSetStatusMap,
  setSensingSystemRequirementsTableRowOrder,
  setSensorBomData, setSensorComponentRequirementsLoadingState,
  setSensorComponentRequirementsTableDataSetStatusMap,
  setSensorComponentRequirementsTableRowOrder,
  setSensorsFromDatabase, setWorldModelSolutionRequirementsLoadingState,
  setWorldModelSolutionRequirementsTableDataSetStatusMap,
  setWorldModelSolutionRequirementsTableRowOrder,
  TCheckboxData,
  updateSensingSystemRequirementsTable,
  updateSensorComponentRequirementsTable,
  updateWorldModelSolutionRequirementsTable
} from "../../../../redux/ducks/AdasPrograms/slice";
import {useSearchParams} from "react-router-dom";
import {selectGlobalDialogState} from "../../../../redux/ducks/GlobalDialog/slice";
import {usePostRequest} from "../../../../api/helper";
import {HorizontalTabs} from "../../../components/Tabs/HorizontalTabs";
import VehicleProgramsTab from "./VehiclePrograms/VehicleProgramsTab";
import SensorBom from "./SensorBom/SensorBom";
import {ArgosFavoriteIconLogic} from "../../../components/Buttons/ArgosFavoriteIconLogic";
import ProgramProperties from "./ProgramProperties/ProgramProperties";
import {SensingSystemRequirements} from "./SensingSystemRequirements/SensingSystemRequirements";
import ADAS_PROGRAMS_CONSTANTS from "../constants";
import {CAdasProgramsRequests} from "./types";
import {toast} from "react-hot-toast";
import useGetHubName from "../../../components/Socket/useGetHubName";
import useSocket from "../../../components/Socket/useSocket";
import {ETrackedItemTypes} from "../../../../constants/trackedItems";
import {ISetRecentlyViewedParams, useSetRecentlyViewedWithDeps} from "../../../../hooks/useSetRecentlyViewedWithDeps";
import {useGetIsUserAdmin} from "../../../../redux/ducks/AccessControl/accessControlHelpers";
import {selectCurrentUser} from "../../../../redux/ducks/AccessControl/slice";
import {WorldModelSolutionRequirements} from "./WorldModelSolutionRequirements/WorldModelSolutionRequirements";
import {
  AdasProgramsReportPages,
  SENSING_SYSTEM_REQUIREMENTS_NAME,
  SENSOR_COMPONENT_REQUIREMENTS_NAME, WORLD_MODEL_SOLUTION_REQUIREMENTS_NAME
} from "./common/constants";
import {SensorComponentRequirements} from "./SensorComponentRequirements/SensorComponentRequirements";

export const adasProgramsItemDetailsSocketSlice = {
    [AdasProgramsReportPages.SENSING_SYSTEM_REQUIREMENT]: {
      setTableRowOrder: setSensingSystemRequirementsTableRowOrder,
      selectTableRowOrder: selectSensingSystemRequirementsTableRowOrder,
      setDataSetStatusMap: setSensingSystemRequirementsTableDataSetStatusMap,
      selectDataSetStatusMap: selectSensingSystemRequirementsTableDataSetStatusMap,
      updateTable: updateSensingSystemRequirementsTable,

      setLoadingState: setSensingSystemRequirementsLoadingState,
      selectLoadingState: selectSensingSystemRequirementsLoadingState,
    },
    [AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT]: {
      setTableRowOrder: setSensorComponentRequirementsTableRowOrder,
      selectTableRowOrder: selectSensorComponentRequirementsTableRowOrder,
      setDataSetStatusMap: setSensorComponentRequirementsTableDataSetStatusMap,
      selectDataSetStatusMap: selectSensorComponentRequirementsTableDataSetStatusMap,
      updateTable: updateSensorComponentRequirementsTable,

      setLoadingState: setSensorComponentRequirementsLoadingState,
      selectLoadingState: selectSensorComponentRequirementsLoadingState,
    },
    [AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT]: {
      setTableRowOrder: setWorldModelSolutionRequirementsTableRowOrder,
      selectTableRowOrder: selectWorldModelSolutionRequirementsTableRowOrder,
      setDataSetStatusMap: setWorldModelSolutionRequirementsTableDataSetStatusMap,
      selectDataSetStatusMap: selectWorldModelSolutionRequirementsTableDataSetStatusMap,
      updateTable: updateWorldModelSolutionRequirementsTable,

      setLoadingState: setWorldModelSolutionRequirementsLoadingState,
      selectLoadingState: selectWorldModelSolutionRequirementsLoadingState,
    },
  }

const AdasProgramsItemDetails = () => {
  const rootRef = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const icons = (
    <div style={{display: 'flex', alignItems: 'center'}}>
      <CloseGlobalDialogButton/>
    </div>
  )
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParams = Object.fromEntries(searchParams)
  const params: any = useAppSelector(state => {
    const dialogState = selectGlobalDialogState(state)
    if (dialogState.isOpen) {
      return dialogState.component?.params ?? urlParams
    } else return urlParams
  })
  const adasProgramId = params?.adasProgramId

  const adasProgramMetaData = useAppSelector(selectAdasProgramsMetaData)
  const fullProgramName = adasProgramMetaData?.fullProgramName
  const isPlanOfRecord: boolean | undefined = adasProgramMetaData?.isPlanOfRecord
  const isProgramArchive: boolean = adasProgramMetaData?.isProgramArchive ?? false
  const creatorGmid: string | undefined = adasProgramMetaData?.creatorGmid
  const gmid = useAppSelector(state => selectCurrentUser(state).id)
  const [isReadOnlyProgram, setIsReadOnlyProgram] = useState<boolean>(false);

  const fetchGeneralAdasProgramData = usePostRequest(CAdasProgramsRequests.fetchGeneralAdasProgramData.url)

  const isAdmin = useGetIsUserAdmin()
  useEffect(() => {
    setIsReadOnlyProgram(isProgramArchive && !(isAdmin || gmid === creatorGmid))
  }, [isProgramArchive, isAdmin, gmid])


  useEffect(() => {
    if (adasProgramId != null) {
      fetchGeneralAdasProgramData({adasProgramId}).then((res) => {
        const {programName, modelYear, fullProgramName, isPlanOfRecord, isProgramArchive, creatorGmid} = res
        dispatch(setAdasProgramsMetaData({adasProgramId,
          programName, modelYear, fullProgramName, isPlanOfRecord, isProgramArchive, creatorGmid}))
      }).catch((err) => {
        console.log(err)
        toast.error('Error fetching adas program data')
      })
    }
  }, [adasProgramId]);

  const fetchHandle = usePostRequest('/HttpTriggerAdasPrograms?requestType=fetchSensorBom&page=sensorBom')
  useEffect(() => {
    if (!adasProgramId) return
    fetchHandle({adasProgramId})
      .then((res) => {
        dispatch(setSensorBomData(res))
      })
      .catch((err) => {
        console.error(err)
        dispatch(setSensorBomData(null))
      })
  }, [adasProgramId])

  const [tabs, setTabs] = useState<any>()

  useEffect(() => {
    const sensorBomTab = {
      name: "Sensor Bill of Materials",
      content: <SensorBom/>
    }
    const sensingSystemRequirementsTab = {
      name: SENSING_SYSTEM_REQUIREMENTS_NAME,
      content: <SensingSystemRequirements />
    }
    const sensorComponentRequirementsTab = {
      name: SENSOR_COMPONENT_REQUIREMENTS_NAME,
      content: <SensorComponentRequirements />
    }
    const worldModelSolutionRequirementsTab = {
      name: WORLD_MODEL_SOLUTION_REQUIREMENTS_NAME,
      content: <WorldModelSolutionRequirements />
    }
    const vehicleProgramsTab = {
      name: "Vehicle Programs",
      content: <VehicleProgramsTab adasProgramId={adasProgramId} />
    }
    const programPropertiesTab = {
      name: "Program Properties",
      content: <ProgramProperties adasProgramId={adasProgramId}/>
    }
    setTabs([sensorBomTab, sensingSystemRequirementsTab, sensorComponentRequirementsTab, worldModelSolutionRequirementsTab, vehicleProgramsTab, programPropertiesTab])
  }, [adasProgramId, isReadOnlyProgram])

  const fetchCoverageRequirementsStudiesHandle = usePostRequest('/HttpTriggerAdasPrograms?requestType=fetchStudies&page=coverageRequirements')
  useEffect(() => {
    fetchCoverageRequirementsStudiesHandle()
      .then((res) => {
        if (res) {
          let coverageRequirementsStudies: any = {}
          res?.forEach((item: any) => {
            coverageRequirementsStudies[item.id] = item
          })
          dispatch(setCoverageRequirementsStudies(coverageRequirementsStudies))
        }
      }).catch((err) => {
      console.log(err)
    })
  }, [])

  const checkboxes: TCheckboxData = useAppSelector(state => state.adasPrograms.sensorBom.sensorBomVisibleSensorsCheckboxes)
  /**
   * NOTE: This data is used for selecting sensors to add, therefore you need to refresh page to see new sensors.
   */
  const fetchSensorsFromDatabaseHandle = usePostRequest('/HttpTriggerAdasPrograms?page=sensorBom&requestType=fetchSensorsForDropdown')
  useEffect(() => {
    fetchSensorsFromDatabaseHandle().then((response: any) => {
      dispatch(setSensorsFromDatabase(response))
    }).catch((error: any) => {
      console.log(error)
    })
  }, [])

  /**
   * Favorites and recently viewed
   */
  const trackedItemUid = adasProgramId
  const favIcon = <ArgosFavoriteIconLogic favoriteType={ETrackedItemTypes.ADAS_PROGRAM} trackedItemUid={trackedItemUid} partitionKey={trackedItemUid}/>
  const recentlyViewedParams: ISetRecentlyViewedParams = {
    trackedItemUid,
    partitionKey: trackedItemUid,
    trackedItemType: ETrackedItemTypes.ADAS_PROGRAM,
  }
  useSetRecentlyViewedWithDeps(recentlyViewedParams)

  const onMessage = useCallback((msg: any) => {
    const page: AdasProgramsReportPages = msg.page
    const req: EAdasProgramsSocketRequestTypes = msg.requestType
    switch (req) {
      case EAdasProgramsSocketRequestTypes.SET_LOADING_STATE: {
        dispatch(adasProgramsItemDetailsSocketSlice[page].setLoadingState(msg.data))
        break
      }
      case EAdasProgramsSocketRequestTypes.UPDATE_TABLE_ROWS: {
        dispatch(adasProgramsItemDetailsSocketSlice[page].updateTable(msg))
        break
      }
      case EAdasProgramsSocketRequestTypes.DELETE_TABLE_ROWS: {
        dispatch(adasProgramsItemDetailsSocketSlice[page].updateTable(msg))
        break
      }
      case EAdasProgramsSocketRequestTypes.DATA_SET_STATUS_MAP: {
        const {dataSetStatusMap} = msg.data
        dispatch(adasProgramsItemDetailsSocketSlice[page].setDataSetStatusMap({dataSetStatusMap}))
        break
      }
      case EAdasProgramsSocketRequestTypes.TABLE_ROW_ORDER: {
        const {newReportOrder} = msg.data
        dispatch(adasProgramsItemDetailsSocketSlice[page].setTableRowOrder(newReportOrder))
        break
      }
    }
  }, [])

  const hubName = useGetHubName(`adasPrograms_${adasProgramId}`)
  useSocket(hubName, onMessage)

  const planOfRecordStatusForTitle = `${isPlanOfRecord ? "" : "Not"} Plan Of Record ${isProgramArchive ? "(Archived)":""}`
  useEffect(() => {
    if(fullProgramName)
      document.title = ADAS_PROGRAMS_CONSTANTS.sidebar.title + ' - ' + fullProgramName;
    return () => {
      document.title = ADAS_PROGRAMS_CONSTANTS.sidebar.title + ' - Landing';
    };
  }, [fullProgramName]);
  return (
    <div
      id={'rootRef'} ref={rootRef}
      style={{
        height: "100%", width: '100%', display: "flex", flexDirection: "column", overflow: 'auto',
        backgroundColor: 'white'
      }}>
      <div>
        <Box sx={{pb: 0}}>
          <div>
            <Toolbar variant="dense" style={{
              alignItems: "center",
              justifyContent: "space-between",
              background: theme.layout.sidebar.backgroundColor
            }}>
              <Typography sx={{
                color: '#ffffff',
                fontSize: 14,
                width: 490,
                marginTop: 0.5
              }}>{`ADAS Program: ${fullProgramName} - ${planOfRecordStatusForTitle}` }</Typography>
              {icons}
            </Toolbar>
          </div>
        </Box>
      </div>
      <ArgosFlexbox vertical style={{flex: 1, width: '100%', overflow: 'auto'}}>
        <HorizontalTabs initContent={null} initHtabIndex={0} initHtabName={'Sensor Bill of Materials'}
                        horizontalTabs={tabs} align={'left'}
                        uid={ADAS_PROGRAMS_CONSTANTS.itemDetails.horizontalTabs.pageHtabs.tabIndexUrlKey}
                        contentDivProps={{margin: 0, padding: 0}} favIcon={favIcon}
                        tabNameUrlKey={ADAS_PROGRAMS_CONSTANTS.itemDetails.horizontalTabs.pageHtabs.tabNameUrlKey}
        />
      </ArgosFlexbox>
    </div>
  )
}

export default AdasProgramsItemDetails